import axios from '@/plugins/axios'

let config: IConfig

interface IConfig {
  OCR_ALLOWED_REGIONS?: string[]
  ALL_ALLOWED_BUILDING_TYPES?: string[]
  ALLOWED_BUILDING_TYPES?: string[]
  ALLOW_ENERGY_RENOVATION?: boolean
  ALLOW_EPC_PROXY?: boolean
  ALLOW_ERS_REPORT_UPLOAD?: boolean
  ALLOW_REFINANCING?: boolean
  ALLOW_RENOVATION?: boolean
  ALLOW_VALUER_ACCREDITATION_EDITING?: boolean
  ALL_LANGS?: string[]
  ASK_FOR_LOAN_AMOUNT?: boolean
  ASK_PROPERTY_DESTINATION?: boolean
  AUTH_API_URL?: string
  AUTOREFRESH_STALE_FRONTEND?: boolean
  BORROWER_PAYS_FOR_VAL?: boolean
  BUILDING_TYPES_WITH_MANUAL_OVM_REPORTS?: string[]
  CATALOG_INTERACTIVE?: boolean
  CATALOG_URL?: string
  CHECK_FLOOD_FOR?: string[]
  CHECK_LOGIN?: boolean
  COOKIE_POLLING_INTERVAL?: number
  COOKIE_PREFIX?: string
  COLLATERAL_DIRECT_ONSITE?: boolean
  CRONOFY_CLIENT_ID?: string
  CUSTOMER_REF_FORBIDDEN_SUBSTRING?: boolean | string
  CUSTOMER_REF_MAX_LENGTH?: number
  CUSTOMER_REF_NEEDED_FOR?: string[]
  CUSTOMER_REF_NOT_NEEDED?: boolean
  CUSTOMER_REF_NUMERIC?: boolean
  CUSTOMER_REF_PLACEHOLDER?: string
  CUSTOMER_SSO_ENABLED?: boolean
  DEFAULT_HOMEPAGE?: string
  DEMO_REPORT?: boolean
  DEV_PERSONAS?: string[]
  DISPATCHED_DEPLOYMENTS?: string[]
  DONT_REQUIRE_EPC_AT_SUBMISSION?: boolean
  DVM_FEE_EURO?: number
  DVM_MAX_TRANSACTION_VALUE_EURO?: number
  DVM_RENOVATION_FEE_EURO?: number
  ENABLED_APPS?: string[]
  ENABLE_CHAT?: boolean
  ENABLE_DEV_FEATURES?: boolean
  ENABLE_DOCUMENTS_UPLOAD?: boolean
  ENABLE_DVM?: boolean
  ENABLE_NEW_DVM?: boolean
  ENABLE_HUBSPOT?: boolean
  ENABLE_OVM?: boolean
  ENABLE_PLAUSIBLE?: boolean
  ENABLE_REGISTRATION_RIGHTS?: boolean
  ENABLE_MANAGED_EVENTS?: boolean
  ENVIRONMENT?: string
  ENVIRONMENT_TYPE?: string
  EPC_BASED_ERS_ENABLED?: boolean
  ERS_LOCK_CUSTOM_PACK_FOR_BORROWERS?: boolean
  ERS_NO_CONSUMPTION_DATA?: boolean
  EPC_PROXY_REQUIRES_ERS_REPORT?: boolean
  EPC_PROXY_REQUIRES_REFINANCING?: boolean
  ESTIM_DATA_SCHEMA_DATE: string
  FEEDBACK_FORM?: string
  FIXED_VALUATION_PRICING?: boolean
  FLOOD_EXTRA_ALLOWED_BUILDING_TYPES?: string[]
  FLOOD_ZONE_STYLE?: Record<
    string,
    Record<string, { fillColor: string; fillOpacity: number; weight: number }>
  >
  FLOW_TYPE?: string
  FRONTEND_URL?: string
  FULL_RENOVATION_ENABLED?: boolean
  GOOGLE_MAP_KEY?: string
  HIDDEN_FEATURES?: string[]
  HIDDEN_FLOOD_TYPES?: string[]
  HIDE_FRONTEND_ERRORS?: boolean
  INSURANCE_EXCLUSION_BUFFER_1M?: boolean
  LOCALE_OVERWRITE?: string
  MANDATORY_EPC_CERTIFICATE?: boolean
  MAPBOX_TOKEN?: string
  NEW_MANDATORY_DOCS?: boolean
  NO_TX_AT_SUBMISSION?: boolean
  NO_TX_FROM_BORROWER?: boolean
  ONSITE_PAYMENT_THRESHOLDS?: number[][]
  ORG_CAN_PAY_FOR_VAL?: boolean
  OVM_MIN_TRANSACTION_VALUE_EURO?: number
  OWNER_CAN_PAY_FOR_VAL?: boolean
  PREFERRED_CATALOG_VIEW?: string
  RECONSTRUCTION_VALUE_ENABLED?: boolean
  REFINANCING_TX_INDEXING?: boolean
  REPORT_VISIBLE?: string[]
  REQUIRE_OWNER_REVIEW_FOR_DVM?: boolean
  RESIDENTIAL_AREA_MINIMUM_PROPORTION?: number
  ROCKESTATE_SSO_ENABLED?: boolean
  SENTRY_DSN?: string
  SHOW_ALL_REQUESTS?: boolean
  SHOW_AVM_INTERVAL?: boolean
  SHOW_DATE_FILTERS?: boolean
  SHOW_FLOOR_DIAGRAM?: boolean
  SHOW_LOADER_MANIPULATION?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  SSO_CONFIG?: any[]
  STRIPE_PUBLISHABLE_KEY?: string
  SUBSTITUTE_FEATURE_ENABLED?: boolean
  SUPPORT_CONTACT?: boolean
  TALKJS_APP_ID?: string
  TEST_MODE?: boolean
  THEME_ID?: string
  TRACK?: boolean
  TRANSACTION_VAL_CONSISTENCY_CHECK?: boolean
  TX_MANDATORY_FOR_SUBMISSION?: boolean
  USE_TX_RANGES_IN_SUBMISSION?: boolean
  VALUATION_API_URL?: string
  VALUATION_CUSTOMER_REF_ALT?: boolean
  source?: 'online' | 'offline'
  isDefault?: boolean
}

// Initialization function to be called at app startup because it's async
export async function initConfig() {
  const defaultConfig = {}
  let data: IConfig | null = null
  try {
    const response = await axios.get('/re_info/frontend_settings', {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
      params: {
        t: Date.now(),
      },
    })
    data = response.data
    localStorage.setItem('re-config', JSON.stringify(data))
    if (data) {
      data.source = 'online'
    }
    console.log('⚙️ Loading config online:', data)
  } catch (e) {
    console.warn('Error while fetching config', e)
    const cachedConfig = localStorage.getItem('re-config')
    if (cachedConfig) {
      data = JSON.parse(cachedConfig)
      if (data) {
        data.source = 'offline'
      }
      console.log('⚙️ Error while fetching config, loading cached version')
    } else {
      console.warn('⚙️ Error while fetching config, no cached version found')
      throw e
    }
  } finally {
    Object.assign(defaultConfig, data)
  }

  const localConfig = JSON.parse(localStorage.getItem('re-local-config') || '{}')
  const finalConfig = {
    _defaultConfig: defaultConfig,
    ...defaultConfig,
    ...localConfig,
    isDefault: Object.keys(localConfig).length === 0,
  }

  config = finalConfig
}

export function useConfig() {
  return config
}
